import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { PAGE_SIZE_OPTIONS } from '../../../shared/components/ui/pretty-select/common-options';
import { PrettySelectComponent } from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { UserCellComponent } from '../../../shared/components/ui/user-cell/user-cell.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { rsqlTextSearch } from '../../../shared/helpers/rsql';
import { OwnersService } from '../../../shared/services/owners.service';
import { VehicleOwner } from '../../../shared/types/owner.types';

/** Fields the search bar runs an OR-contains against (server-side RSQL). */
const OWNERS_SEARCH_FIELDS = [
  'businessName',
  'businessEmail',
  'businessPhone',
] as const;

@Component({
  selector: 'app-owners-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    PageHeaderComponent,
    DataStateComponent,
    PrettySelectComponent,
    UserCellComponent,
  ],
  templateUrl: './owners-list.component.html',
})
export class OwnersListComponent implements OnInit {
  private readonly ownersService = inject(OwnersService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly owners = signal<VehicleOwner[]>([]);
  readonly totalItems = signal(0);
  readonly totalPages = signal(0);
  readonly page = signal(1);
  readonly size = signal(10);

  readonly searchCtrl = this.fb.control('');
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  readonly pageWindow = computed<number[]>(() => {
    const total = this.totalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.page();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.ownersService
      .list({
        page: this.page(),
        size: this.size(),
        search:
          rsqlTextSearch(OWNERS_SEARCH_FIELDS, this.searchCtrl.value) ??
          undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.owners.set(res.data ?? []);
          this.totalItems.set(res.totalItems ?? 0);
          this.totalPages.set(res.totalPages ?? 0);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.owners.set([]);
          this.totalItems.set(0);
          this.totalPages.set(0);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages() || p === this.page()) return;
    this.page.set(p);
    this.load();
  }

  changeSize(s: number): void {
    if (!s || s === this.size()) return;
    this.size.set(s);
    this.page.set(1);
    this.load();
  }
}
